import { getLooseObjectValue, resolveDataValue } from "./CssHelper";
import { defaultListFnConfig } from "./exports";
import * as XLSX from "xlsx";
export function resolveValue(node, dataCtx, runtimeVars) {
  if (!node.dynamic || !node.dynamicKey) return node.staticText || "";
  try {
    const key = node.dynamicKey;
    let val = null;
    
    // First, check if this is a runtime variable (from input elements)
    if (runtimeVars) {
      const runtimeVal=getLooseObjectValue(runtimeVars,key);
      if(runtimeVal!==undefined) val = runtimeVal;
    }
    if (val === null || val === undefined) {
      // Otherwise, resolve from data context (group data)
      val = resolveDataValue(key,dataCtx);
    }
    
    if (val == null) return node.dynamicFallback || "";
    if (node.dynamicTransform) { try { val = new Function("value", "return " + node.dynamicTransform)(val); } catch (_) {} }
    return String(val);
  } catch (_) { return node.dynamicFallback || ""; }
}

export function normalizeListFnConfig(config = {}) {
  return { ...defaultListFnConfig(), ...(config || {}) };
}

export function getListKeysFromArray(items) {
  if (!Array.isArray(items) || !items.length) return [];
  const firstObj = items.find((item) => item && typeof item === "object" && !Array.isArray(item));
  return firstObj ? Object.keys(firstObj) : [];
}

export function normalizeForCompare(value, type) {
  if (value == null) return null;
  if (type === "number") {
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }
  if (type === "date") {
    const d = new Date(value).getTime();
    return Number.isNaN(d) ? null : d;
  }
  if (type === "string") return String(value);
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  const maybeNum = Number(value);
  if (!Number.isNaN(maybeNum) && value !== "") return maybeNum;
  const maybeDate = new Date(value).getTime();
  if (!Number.isNaN(maybeDate) && typeof value === "string" && value.includes("-")) return maybeDate;
  return String(value);
}

export function compareValues(a, b, direction) {
  if (a == null && b == null) return 0;
  if (a == null) return direction === "asc" ? 1 : -1;
  if (b == null) return direction === "asc" ? -1 : 1;
  if (typeof a === "number" && typeof b === "number") return direction === "asc" ? a - b : b - a;
  const left = String(a).toLowerCase();
  const right = String(b).toLowerCase();
  if (left === right) return 0;
  if (direction === "asc") return left > right ? 1 : -1;
  return left < right ? 1 : -1;
}

export function matchesFilter(itemVal, filterVal, mode, caseSensitive) {
  const leftRaw = itemVal == null ? "" : itemVal;
  const rightRaw = filterVal == null ? "" : filterVal;
  const left = caseSensitive ? String(leftRaw) : String(leftRaw).toLowerCase();
  const right = caseSensitive ? String(rightRaw) : String(rightRaw).toLowerCase();

  if (mode === "equals") return left === right;
  if (mode === "startsWith") return left.startsWith(right);
  if (mode === "endsWith") return left.endsWith(right);
  if (mode === "gt" || mode === "gte" || mode === "lt" || mode === "lte") {
    const ln = Number(leftRaw);
    const rn = Number(rightRaw);
    if (Number.isNaN(ln) || Number.isNaN(rn)) return false;
    if (mode === "gt") return ln > rn;
    if (mode === "gte") return ln >= rn;
    if (mode === "lt") return ln < rn;
    return ln <= rn;
  }
  return left.includes(right);
}

export function applyListFunctions(items, config, runtimeVars = {}) {
  if (!Array.isArray(items)) return items;
  const cfg = normalizeListFnConfig(config);
  if (!cfg.enabled) return items;

  let next = [...items];

  if (cfg.filterEnabled && cfg.filterKey) {
    const filterValue = cfg.filterValueSource === "variable"
      ? runtimeVars?.[cfg.filterVarName] ?? ""
      : cfg.filterValue;
    next = next.filter((item) => {
      const itemVal = resolveDataValue(cfg.filterKey, item);
      return matchesFilter(itemVal, filterValue, cfg.filterMode, cfg.filterCaseSensitive);
    });
  }

  if (cfg.sortEnabled && cfg.sortKey) {
    const sortType = cfg.sortType || "auto";
    const direction = cfg.sortDirection === "desc" ? "desc" : "asc";
    next.sort((a, b) => {
      const av = normalizeForCompare(resolveDataValue(cfg.sortKey, a), sortType);
      const bv = normalizeForCompare(resolveDataValue(cfg.sortKey, b), sortType);
      return compareValues(av, bv, direction);
    });
  }

  if (cfg.customEnabled && cfg.customTransform && cfg.customTransform.trim()) {
    try {
      const fn = new Function("items", "vars", cfg.customTransform);
      const out = fn([...next], runtimeVars || {});
      if (Array.isArray(out)) next = out;
    } catch (_err) {
      // Ignore custom function errors to avoid breaking rendering.
    }
  }

  return next;
}

export function parseExcelSheetFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        if (!rows || !rows.length) return resolve([]);
        if (rows.length === 1) return resolve(rows[0].map(cell => cell === null || cell === undefined ? "" : String(cell)));
        const headers = rows[0].map(h => String(h || "").trim());
        const hasHeaders = headers.some(h => h !== "");
        if (rows[0].length === 1 && !hasHeaders) {
          return resolve(rows.slice(1).map(r => r[0] === undefined ? "" : String(r[0])));
        }
        resolve(rows.slice(1).map(row => {
          const obj = {};
          headers.forEach((h, idx) => { obj[h || `col${idx + 1}`] = row[idx] === undefined ? "" : row[idx]; });
          return obj;
        }));
      } catch (err) { reject(err); }
    };
    reader.onerror = (e) => reject(e);
    reader.readAsArrayBuffer(file);
  });
}
